/**
 * @file BlendSaveModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-14
 * @modified 2026-08-14
 * @description Save the working blend — name it, type it, and when one is
 *              already open, choose whether this replaces it or becomes a new
 *              blend.
 *
 *              That choice is the reason this is a modal rather than a prompt.
 *              After loading a saved blend and changing the recipe, "save" is
 *              genuinely ambiguous: a new version of the same blend, or a
 *              separate blend derived from it. Guessing either way is wrong
 *              often enough to be worth one click.
 */

import React, { useCallback, useEffect, useState } from 'react';
import Modal from './Modal';
import Input from './Input';
import Dropdown from './Dropdown';
import TickCheckbox from './TickCheckbox';
import { useSlideOutButton } from './SlideOutButton';
import { BLEND_TYPE_OPTIONS, guessBlendType } from '../utils/blendTypes';
import './BlendCalculator.css';

function SaveBody({ mode, setMode, openBlend, name, setName, blendType, setBlendType,
                    notes, setNotes, error, saving }) {
  useSlideOutButton([mode, saving]);

  return (
    <div className="blend-save-body">
      {error && (
        <div className="blend-warning inline">
          <span className="material-symbols-rounded">error</span>
          <span>{error}</span>
        </div>
      )}

      {openBlend && (
        <div className="blend-save-choice">
          <label className={`blend-save-option${mode === 'update' ? ' on' : ''}`}>
            <TickCheckbox
              id="blend-save-update"
              checked={mode === 'update'}
              onChange={() => setMode('update')}
              ariaLabel={`Update ${openBlend.name}`}
            />
            <div>
              <strong>Update “{openBlend.name}”</strong>
              <span>
                Adds a new version to the blend you opened. Its history is kept —
                nothing is overwritten.
              </span>
            </div>
          </label>

          <label className={`blend-save-option${mode === 'new' ? ' on' : ''}`}>
            <TickCheckbox
              id="blend-save-new"
              checked={mode === 'new'}
              onChange={() => setMode('new')}
              ariaLabel="Save as a new blend"
            />
            <div>
              <strong>Save as a new blend</strong>
              <span>
                Leaves “{openBlend.name}” untouched and keeps this recipe separately.
              </span>
            </div>
          </label>
        </div>
      )}

      <div className="filter-group">
        <label htmlFor="blend-save-name">Name *</label>
        <Input
          id="blend-save-name"
          type="text"
          value={name}
          placeholder="e.g. TMB 18 Blend"
          disabled={mode === 'update'}
          onChange={(e) => setName(e.target.value)}
        />
        {mode === 'update' && (
          <span className="blend-field-hint">
            Updating keeps the existing name. Choose “Save as a new blend” to rename.
          </span>
        )}
      </div>

      <div className="filter-group">
        <label>Blend type</label>
        <Dropdown options={BLEND_TYPE_OPTIONS} value={blendType} onChange={setBlendType} />
        <span className="blend-field-hint">
          What class of stock this is for. Used to group and filter saved blends.
        </span>
      </div>

      <div className="filter-group">
        <label htmlFor="blend-save-notes">Notes</label>
        <Input
          id="blend-save-notes"
          type="text"
          value={notes}
          placeholder="What changed, or why — optional"
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
    </div>
  );
}

export default function BlendSaveModal({ openBlend, onClose, onSave, saving, error }) {
  const [mode, setMode] = useState(openBlend ? 'update' : 'new');
  const [name, setName] = useState(openBlend?.name || '');
  const [blendType, setBlendType] = useState(openBlend?.blend_type || '');
  const [notes, setNotes] = useState('');
  const [localError, setLocalError] = useState('');

  // Switching to "new" starts from a copy of the open blend's name, which is
  // almost always the base someone edits ("… V2", "… June 26").
  useEffect(() => {
    if (mode === 'new' && openBlend && name === openBlend.name) {
      setName(`${openBlend.name} (copy)`);
    }
    if (mode === 'update' && openBlend) {
      setName(openBlend.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Only a pre-fill, and only when nothing is set — never overwrite a choice.
  useEffect(() => {
    if (!blendType) {
      const guess = guessBlendType(name);
      if (guess) setBlendType(guess);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  const submit = useCallback(() => {
    const trimmed = name.trim();
    if (mode === 'new' && !trimmed) {
      setLocalError('Give the blend a name.');
      return;
    }
    setLocalError('');
    onSave({
      mode,
      id: mode === 'update' ? openBlend?.id : null,
      name: trimmed || openBlend?.name,
      blend_type: blendType || null,
      notes: notes.trim() || null
    });
  }, [mode, name, blendType, notes, openBlend, onSave]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={openBlend ? 'Save this blend' : 'Save this blend'}
      size="sm"
      className="blend-save"
      footer={
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-round" onClick={submit} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
              <span className="btn-label">
                {saving ? 'Saving…' : (mode === 'update' ? 'Update' : 'Save')}
              </span>
            </button>
          </div>
        </div>
      }
    >
      <SaveBody
        mode={mode} setMode={setMode} openBlend={openBlend}
        name={name} setName={setName}
        blendType={blendType} setBlendType={setBlendType}
        notes={notes} setNotes={setNotes}
        error={localError || error} saving={saving}
      />
    </Modal>
  );
}
